import React, { useState, useEffect, useRef } from 'react';
import qaData from '../assets/qa.json';
import javaData from '../assets/java.json';
import databaseData from '../assets/database.json';
import dotnetData from '../assets/dotnet.json';
/* import { mockAssessmentResult } from '../mockAssessmentResult.js'; */
import CustomMarkdownRenderer from '../components/CustomMarkdownRenderer.js';
import mammoth from 'mammoth';

function InternalUser() {
  const [selectedSkill, setSelectedSkill] = useState('');
  const [jdFile, setJdFile] = useState(null);
  const [candidateFile, setCandidateFile] = useState(null);
  const [jdContent, setJdContent] = useState('');
  const [candidateContent, setCandidateContent] = useState('');
  const [showJdTooltip, setShowJdTooltip] = useState(false);
  const [showCandidateTooltip, setShowCandidateTooltip] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [skillData, setSkillData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const users = ['user A', 'user M', 'user G']; // Dummy data for users
  const projects = ['ProA', 'ProB', 'ProC']; // Dummy data for projects
  const skills = ['QA', 'Java', 'Database', '.NET'];
  const [assessmentResult, setAssessmentResult] = useState(null);

  const dropdownRef = useRef(null); // Create a ref for the dropdown

  const handleSkillSelect = (skill) => {
    setSelectedSkill(skill);
    setShowDropdown(false);
    let data;
    switch (skill) {
      case 'QA':
        data = qaData;
        break;
      case 'Java':
        data = javaData;
        break;
      case 'Database':
        data = databaseData;
        break;
      case '.NET':
        data = dotnetData;
        break;
      default:
        data = null;
    }
    setSkillData(data);
  };

  const validFileTypes = ['text/plain', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

  const handleJdFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!validFileTypes.includes(file.type)) {
        setErrorMessage('Please upload a file in .txt or .docx format.');
        setJdFile(null);
        setJdContent('');
        return;
      }
      setErrorMessage(''); // Clear previous error
      setJdFile(file);

      if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        mammoth.extractRawText({ arrayBuffer: file })
          .then(result => {
            setJdContent(result.value);
          })
          .catch(err => console.error('Error reading JD file:', err));
      } else {
        const reader = new FileReader();
        reader.onload = (e) => {
          setJdContent(e.target.result);
        };
        reader.readAsText(file);
      }
    }
  };

  const handleCandidateFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!validFileTypes.includes(file.type)) {
        setErrorMessage('Please upload a file in .txt or .docx format.');
        setCandidateFile(null);
        setCandidateContent('');
        return;
      }
      setErrorMessage(''); // Clear previous error
      setCandidateFile(file);

      if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        mammoth.extractRawText({ arrayBuffer: file })
          .then(result => {
            setCandidateContent(result.value);
          })
          .catch(err => console.error('Error reading Candidate file:', err));
      } else {
        const reader = new FileReader();
        reader.onload = (e) => {
          setCandidateContent(e.target.result);
        };
        reader.readAsText(file);
      }
    }
  };

  const handleAssessClick = async () => {
    const dataToSend = {
      jdContent,
      skillData,
      candidateContent,
    };

    // Mock response for testing
    try {
      setTimeout(() => {
        console.log('Mock Assessment Result:', mockAssessmentResult);
        setAssessmentResult(mockAssessmentResult);
      }, 1000);
    } catch (error) {
      console.error('Error during assessment:', error);
    }
  };

  const handleReset = () => {
    setSelectedSkill('');
    setJdFile(null);
    setCandidateFile(null);
    setJdContent('');
    setCandidateContent('');
    setSkillData(null);
    setSelectedUser('');
    setSelectedProject('');
    setErrorMessage(''); // Clear error message
  };

  const isAssessButtonEnabled = selectedSkill && candidateFile;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);

  return (
    <div>
      {/* Display error message if exists */}
      {errorMessage && <div className="error-message" style={{ color: 'red' }}>{errorMessage}</div>}
      {/* Step 1: Search or type the user and project */}
      <div className="labels">
        <div className="input-section">
          {/* User Input Field */}
          <label>Step 1: Search or Type the User</label>
          <input
            type="text"
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            placeholder="Search or type user"
            list="user-options"
            class="input-spacing"
          />
          <datalist id="user-options">
            {users.map((user, index) => (
              <option key={index} value={user} />
            ))}
          </datalist>
        </div>
        <div>
          {/* Project Input Field */}
          <label>Step 2: Search or Type the Project</label>
          <input
            type="text"
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            placeholder="Search or type project"
            list="project-options"
            class="input-spacing"
          />
          <datalist id="project-options">
            {projects.map((project, index) => (
              <option key={index} value={project} />
            ))}
          </datalist>
        </div>
      </div>

      {/* Step 3: Select Skill to Evaluate */}
      <div className="labels">
        <div className="upload-section" ref={dropdownRef}>
          <label>Step 3. Select Skill to Evaluate</label>
          <div className="custom-dropdown">
            <div className="dropdown-toggle" onClick={() => setShowDropdown((prev) => !prev)}>
              {selectedSkill || 'Select Skill'}
            </div>
            <div className={`dropdown-menu ${showDropdown ? 'show' : ''}`}>
              {skills.map((skill) => (
                <div
                  key={skill}
                  className="dropdown-item"
                  onClick={() => handleSkillSelect(skill)}
                >
                  {skill}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Step 4: Upload JD */}
      <div className="labels">
        <div className="upload-section">
          <label htmlFor="jd-upload" className="button">Step 4. Click to Upload Job Description</label>
          <input
            id="jd-upload"
            type="file"
            accept=".txt, .docx"
            onChange={handleJdFileChange}
            style={{ display: 'none' }}
          />
          {jdFile && (
            <div 
              onMouseEnter={() => setShowJdTooltip(true)} 
              onMouseLeave={() => setShowJdTooltip(false)}
              style={{ position: 'relative', display: 'inline-block' }}
            >
              <p class="uploaded-jd">Uploaded JD: {jdFile.name}</p>
              {showJdTooltip && (
                <div className="tooltip">
                  <p>{jdContent.length > 100 ? jdContent.slice(0, 100) + '...' : jdContent}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Step 5: Upload Candidate File */}
      <div className="labels">
        <div className="upload-section">
          <label htmlFor="candidate-upload" className="button">Step 5. Click to Upload Candidate's Interview File</label>
          <input
            id="candidate-upload"
            type="file"
            accept=".txt, .docx"
            onChange={handleCandidateFileChange}
            style={{ display: 'none' }}
          />
          {candidateFile && (
            <div
              onMouseEnter={() => setShowCandidateTooltip(true)}
              onMouseLeave={() => setShowCandidateTooltip(false)}
              style={{ position: 'relative', display: 'inline-block' }}
            >
              <p class="uploaded-jd">Uploaded Candidate File: {candidateFile.name}</p>
              {showCandidateTooltip && (
                <div className="tooltip">
                  <p>{candidateContent.length > 100 ? candidateContent.slice(0, 100) + '...' : candidateContent}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Step 6: Assess and Reset Buttons */}
      <div style={{ marginTop: '0.9cm' }}>
        <div className="bottom-section">
          <button
            onClick={handleAssessClick}
            disabled={!isAssessButtonEnabled}
            style={{ backgroundColor: isAssessButtonEnabled ? '#4CAF50' : '#ccc', color: 'white' }}
          >
            Click to Assess
          </button>
          <button
            onClick={handleReset}
            style={{ backgroundColor: '#f44336', color: 'white', marginLeft: '10px' }}
          >
            Reset
          </button>
        </div>
      </div>

      {/* Display assessment results */}
      {assessmentResult && (
        <div style={{ marginTop: '1cm' }}>
          <h3>Assessment Result:</h3>
          <CustomMarkdownRenderer markdown={assessmentResult} />
        </div>
      )}
    </div>
  );
}

export default InternalUser;
